/**
 * 검수 가이드 데이터 (홈 체크리스트 + 요구사항·이슈 히스토리).
 *
 * 백엔드 없이 프론트에서 관리하는 정적 데이터다. 새 요구사항/이슈/문의가
 * 생기면 이 파일의 배열만 갱신하면 화면에 즉시 반영된다.
 *
 * 다국어 정책: 각 문자열을 ``{ ko, ja }`` 로 보유하고, 화면에서
 * 현재 언어(``i18n.resolvedLanguage``)에 맞춰 선택한다. (ja default)
 */

/** 언어별 문자열 쌍. */
export interface Bilingual {
  ko: string;
  ja: string;
}

/** 현재 언어에 맞는 문자열을 고른다. ko 가 아니면 ja 로 폴백. */
export function pickLang(text: Bilingual, lang: string | undefined): string {
  return lang === "ko" ? text.ko : text.ja;
}

/** 검수 시 체크리스트 항목 (홈 화면 상시 리마인드). */
export interface ChecklistItem {
  id: number;
  text: Bilingual;
}

export const REVIEW_CHECKLIST: ChecklistItem[] = [
  {
    id: 1,
    text: {
      ko: "누락된 항목이 없는가?",
      ja: "欠落している項目はないか？",
    },
  },
  {
    id: 2,
    text: {
      ko: "누락이 된 항목은 실제로 없는 항목인가?",
      ja: "欠落している項目は、実際に存在しない項目か？",
    },
  },
  {
    id: 3,
    text: {
      ko: "대리인명은 실제 영수증 대리인과 일치하는가?",
      ja: "代理人名は実際のインボイスの代理人と一致するか？",
    },
  },
  {
    id: 4,
    text: {
      ko: "유효한 최종 통화 단위로 기입되어 있는가?",
      ja: "有効な最終通貨単位で記入されているか？",
    },
  },
  {
    id: 5,
    text: {
      ko: "최종 지불 금액은, 유효통화 단위 기준으로 기입되었는가?",
      ja: "最終支払金額は、有効な通貨単位基準で記入されているか？",
    },
  },
  {
    id: 6,
    text: {
      ko: "SWIFT 코드가 유효 통화 단위를 따르고 있는가?",
      ja: "SWIFTコードは有効な通貨単位に従っているか？",
    },
  },
  {
    id: 7,
    text: {
      ko: "원천 징수액과 최종 지불 금액의 합계가 청구금액과 동일한가?",
      ja: "源泉徴収額と最終支払金額の合計が請求金額と一致するか？",
    },
  },
  {
    id: 8,
    text: {
      ko: "SWIFT 코드의 오타는 없는가?",
      ja: "SWIFTコードの誤字はないか？",
    },
  },
];

/** 요구사항/이슈/문의 구분. */
export type RequirementCategory = "requirement" | "issue" | "inquiry";

/** 진행 상태. */
export type RequirementStatus = "done" | "in_progress" | "pending";

/** 요구사항·이슈 히스토리 1건. */
export interface RequirementEntry {
  id: string;
  /** YYYY-MM-DD */
  date: string;
  category: RequirementCategory;
  title: Bilingual;
  detail: Bilingual;
  status: RequirementStatus;
}

/**
 * 요구사항·이슈 히스토리 (최신이 위로 오도록 관리).
 * 새 항목은 배열 상단에 추가한다.
 */
export const REQUIREMENTS_HISTORY: RequirementEntry[] = [
  {
    id: "2026-1016-auto-fix-check-recommended",
    date: "2026-10-16",
    category: "requirement",
    status: "done",
    title: {
      ko: "자동 검수가 고친 건을 목록에 '확인 권장'으로 표시 + 고친 값 확인함",
      ja: "自動検収が修正した案件を一覧に「確認推奨」と表示 + 修正値を確認済み",
    },
    detail: {
      ko: "실무자 피드백: '고친 값 확인 권장'이 보고 메일에만 있고, 도구 목록에서는 초록 '자동 해소'라 끝난 건처럼 보임. 이제 목록 검수 칸에 '사람 확인 필요'와 같은 주황색으로 '확인 권장 · 고친 칸'을 보여 주고(마우스를 올리면 원래 값 → 고친 값), 검수 필터에 '확인 권장'을 추가. 편집 화면에 자동 검수가 고친 값과 칸별 '자동 수정·원래 값'을 표시하고, '고친 값 확인함'을 누르면 확인자·시각을 남기고 표시를 흐리게 바꿈(취소 가능). 2차 완료·납품은 그대로. 보고 메일에도 원래 값 → 고친 값을 적음.",
      ja: "担当者フィードバック: 「修正値の確認を推奨」が報告メールにしかなく、ツールの一覧では緑の「自動修正」で完了した案件のように見えていた。今後は一覧の検査欄に「要確認」と同じオレンジ色で「確認推奨・修正した欄」を表示し（マウスを重ねると元の値 → 修正値）、検査フィルターに「確認推奨」を追加。編集画面に自動検収が修正した値と欄ごとの「自動修正・元の値」を表示し、「修正値を確認済み」を押すと確認者・日時を記録して表示を薄くする（取り消し可）。二次完了・納品はそのまま。報告メールにも元の値 → 修正値を記載。",
    },
  },
  {
    id: "2026-1015-billing-kana-agent-name-display",
    date: "2026-10-15",
    category: "issue",
    status: "done",
    title: {
      ko: "청구처 일본어 표기 오타 자동 정리 + 편집 화면 대리인명을 납품 값으로 표시",
      ja: "請求先の日本語表記の誤りを自動で修正 + 編集画面の代理人名を納品値で表示",
    },
    detail: {
      ko: "실무자 피드백: 청구처를 가장 많이 고치고(운영 168회), 특히 トヨタテクニカルディベロップメント株式会社의 가타카나 오타(작은 ィ·ッ, ブ/プ)가 잦음. 수정제안은 그 건만 다시 읽어 다음 건에 반영되지 않음. 이제 추출·수정제안 결과·편집 저장 때 일본어 표기 오타를 정식 표기로 맞춤(다른 토요타 계열사·영문 표기는 그대로, 이미 저장된 값은 유지). 또 Watson & Band 등 마스터 정식명 끝의 통화 표시 '(YEN)'은 납품 때 빠지므로 편집 화면·작업 표에서도 뺀 값으로 보여 주고, 사람이 지워도 1차 검수가 '마스터 정식명과 다름'으로 지적하지 않음.",
      ja: "担当者フィードバック: 請求先の修正が最も多く（本番168回）、特にトヨタテクニカルディベロップメント株式会社のカタカナの誤り（小さいィ・ッ、ブ/プ）が多い。修正提案はその案件だけ読み直すため次の案件には反映されない。今後は抽出・修正提案の結果・編集保存の際に日本語表記の誤りを正式表記に揃える（他のトヨタ系列会社・英語表記はそのまま、保存済みの値は維持）。また Watson & Band などマスタ正式名称末尾の通貨表示「(YEN)」は納品時に除かれるため、編集画面・作業表でも除いた値で表示し、担当者が削除しても一次検査が「マスタ正式名称と異なる」と指摘しない。",
    },
  },
  {
    id: "2026-1015-review-rejected-records",
    date: "2026-10-15",
    category: "requirement",
    status: "done",
    title: {
      ko: "반려 건도 1차 검수 전체 대상 — 반려 판정이 맞는지까지 AI 검토",
      ja: "却下案件も一次検査の全体対象 — 却下判定が正しいかまでAIレビュー",
    },
    detail: {
      ko: "검수 목적이 1차 작업 전수 조사이므로 반려 건을 검수에서 빼지 않음. 1차 검수 완료(사람)와 자동 검수 모두 반려 건도 AI 검토를 하고, 반려 사유가 실제 값·PDF와 맞는지 확인함(예: 출원인을 청구처로 읽어 '청구처 불일치'로 잘못 반려된 건 → '반려 판정이 맞지 않을 수 있음'으로 사람 확인). 반려 해제는 사람이 확정. 반려 건의 다른 칸 오류(인보이스 번호 등)도 함께 지적됨. 반려 건의 2차 완료도 AI 검토가 끝난 뒤 가능. 예전 규칙으로 AI 검토 없이 1차 완료된 반려 건은 '추가검토 필요'로 바뀌며, 1차 검수를 해제했다가 다시 체크하면 검토됨.",
      ja: "検査の目的が一次作業の全数確認であるため、却下案件を検査から除外しない。一次検査完了（担当者）と自動検査のいずれでも却下案件をAIレビューし、却下理由が実際の値・PDFと合っているか確認する（例: 出願人を請求先と読み「請求先の不一致」で誤って却下された案件 → 「却下判定が正しくない可能性」として担当者確認）。却下の解除は担当者が確定。却下案件の他の欄の誤り（インボイスNoなど）も併せて指摘される。却下案件の二次完了もAIレビュー終了後に可能。旧ルールでAIレビューなしに一次完了となった却下案件は「要追加確認」に変わり、一次検査を解除して再度チェックするとレビューされる。",
    },
  },
  {
    id: "2026-1015-ai-review-found-compare",
    date: "2026-10-15",
    category: "issue",
    status: "done",
    title: {
      ko: "AI 검토: 입력값과 같은 값을 '다르다'고 한 지적은 자동으로 제외 + 자동 검수가 해결한 칸을 버리던 문제 수정",
      ja: "AIレビュー: 入力値と同じ値を「異なる」とした指摘を自動で除外 + 自動検査が解決した欄を捨てていた問題を修正",
    },
    detail: {
      ko: "AI 검토가 입력값과 같은 값을 '다르게 전사됐다'고 지적하는 경우가 있었음(예: S&I International Bangkok — 'PDF 인쇄값(KRTHTHBK)과 다르게 KRTHTHBK로 전사'). 이제 AI가 지적할 때 그 칸에 들어가야 할 값을 함께 받고, 프로그램이 입력값과 기계적으로 비교해 같으면 그 지적은 빼고 기록만 남김(금액은 숫자로, 날짜는 마스터 날짜 순서로 바꿔 비교). 또 자동 검수가 앞 회차에서 해결한 칸(예: 비어 있던 SWIFT)을, 다른 칸의 지적이 남아 멈출 때 함께 버리던 문제를 고침 — 해결된 칸은 반영하고 사람에게 넘김.",
      ja: "AIレビューが入力値と同じ値を「異なって転記された」と指摘する場合があった（例: S&I International Bangkok — 「PDF印字値(KRTHTHBK)と異なりKRTHTHBKと転記」）。今後はAIが指摘する際にその欄に入るべき値も受け取り、プログラムが入力値と機械的に比較して同じであればその指摘を除外し、記録のみ残す（金額は数値、日付はマスタの日付順に変換して比較）。また自動検査が前の回で解決した欄（例: 空だったSWIFT）を、他の欄の指摘が残って停止する際に一緒に捨てていた問題を修正 — 解決した欄は反映して人に引き継ぐ。",
    },
  },
  {
    id: "2026-1015-agent-code-scheme",
    date: "2026-10-15",
    category: "requirement",
    status: "done",
    title: {
      ko: "대리인 ID를 최신 마스터의 5자리 支払先コード로 통일 (예: 010 → 70010)",
      ja: "代理人IDを最新マスタの5桁の支払先コードに統一（例: 010 → 70010）",
    },
    detail: {
      ko: "대리인 ID를 PDF 파일명 현지 대리인 코드의 앞 5자리와 같은 값으로 바꿈(010 → 70010, 1018 → 71018). 미지 대리인은 999 → 79999. 3자리·4자리가 섞여 사람이 1018을 018로 고치던 문제를 없애고, 최신 마스터와 변환 없이 대조함. 대리인 마스터는 DB 하나만 사용(예전 코드 파일 마스터 제거). 전환할 때 정식명 3곳(GABRIEL·Watson & Band(USD)·SHANGHAI PATENT & TRADEMARK)을 08-03 마스터에 맞추고 CPA Global(70164)·Dr. Schon, Neymeyr & Partner(71089)를 추가함. 잘못 고쳐진 ID(018·089)는 파일명 코드로 바로잡음. 납품 CSV에는 대리인 ID가 없어 납품물은 바뀌지 않음(확인용 CSV의 대리인ID 칸만 바뀜).",
      ja: "代理人IDをPDFファイル名の現地代理人コードの先頭5桁と同じ値に変更（010 → 70010、1018 → 71018）。未識別の代理人は999 → 79999。3桁と4桁が混在し、1018を018に直してしまう問題をなくし、最新マスタと変換なしで照合する。代理人マスタはDBのみを使用（旧コードファイルのマスタを削除）。切り替え時に正式名称3社（GABRIEL・Watson & Band(USD)・SHANGHAI PATENT & TRADEMARK）を08-03マスタに合わせ、CPA Global（70164）・Dr. Schon, Neymeyr & Partner（71089）を追加。誤って直されたID（018・089）はファイル名のコードで修正。納品CSVには代理人IDがないため納品物は変わらない（確認用CSVの代理人ID欄のみ変わる）。",
    },
  },
  {
    id: "2026-1015-agent-master-db",
    date: "2026-10-15",
    category: "issue",
    status: "done",
    title: {
      ko: "관리자 화면에서 바꾼 대리인·회사별 추출 규칙이 추출에 반영되지 않던 문제 수정",
      ja: "管理画面で変更した代理人・会社別抽出ルールが抽出に反映されていなかった問題を修正",
    },
    detail: {
      ko: "처리 서버(추출 워커)가 대리인 마스터를 DB에서 읽지 않고 이관 전 기본값만 보고 있었음(2026-05 마스터 DB 이관 이후). 그래서 관리자 화면에서 추가·수정한 대리인과 승인한 회사별 추출 규칙이 추출에 쓰이지 않았음(운영: 회사별 추출 규칙 8곳, 규칙이 생긴 뒤 추출된 약 120건). 처리 서버도 DB 마스터를 읽도록 수정(변경은 1분 안에 반영). 1차 검수의 대리인 대조도 DB 마스터 기준으로 맞춤 — 새로 추가한 대리인이 '마스터에 없음'으로 지적되지 않음.",
      ja: "処理サーバー（抽出ワーカー）が代理人マスタをDBから読まず、移行前の既定値だけを参照していた（2026-05のマスタDB移行以降）。そのため管理画面で追加・修正した代理人と承認した会社別抽出ルールが抽出に使われていなかった（本番: 会社別抽出ルール8社、ルール作成後に抽出された約120件）。処理サーバーもDBのマスタを読むよう修正（変更は1分以内に反映）。一次検査の代理人照合もDBのマスタ基準に統一 — 新しく追加した代理人が「マスタにない」と指摘されない。",
    },
  },
  {
    id: "2026-1015-ref-number-check",
    date: "2026-10-15",
    category: "requirement",
    status: "done",
    title: {
      ko: "TTDC案件番号 비어 있음·형식 불일치를 1차 검수가 지적 (파일명 번호를 힌트로 표시)",
      ja: "TTDC案件番号の空欄・形式不一致を一次検査が指摘（ファイル名の番号をヒントとして表示）",
    },
    detail: {
      ko: "납품 전 품질 검사에서만 경고하던 케이스번호 빈칸·형식 불일치를 1차 검수의 코드 판정으로도 지적. 지적 문장 끝에 파일명의 번호를 '(파일명 번호: TFN221103-IN)'처럼 보여 줌 — PDF를 확인한 뒤 그 번호로 채움. 자동 검수는 PDF를 다시 읽어 PDF 글자에서 확인된 번호만 넣고, 찾지 못하면 사람 확인으로 남김(시스템은 파일명 값을 쓰지 않는 원칙).",
      ja: "納品前品質チェックでのみ警告していた案件番号の空欄・形式不一致を、一次検査のコード判定でも指摘。指摘文の末尾にファイル名の番号を「(ファイル名の番号: TFN221103-IN)」のように表示 — PDFを確認してからその番号で入力。自動検収はPDFを読み直し、PDFの文字で確認できた番号のみ入力し、見つからなければ人の確認に回す（システムはファイル名の値を使わない原則）。",
    },
  },
  {
    id: "2026-1015-amount-notes-refresh",
    date: "2026-10-15",
    category: "issue",
    status: "done",
    title: {
      ko: "금액을 고친 뒤에도 '확인 필요'의 금액 경고가 남던 문제 수정",
      ja: "金額を修正した後も「要確認」の金額警告が残っていた問題を修正",
    },
    detail: {
      ko: "금액 경고(차감 정합성·최종지불 검증·금액 정합성)는 추출 때 한 번만 만들어져, 금액을 맞게 고쳐도 남아 있었고 납품 전 품질 검사의 금액 경고도 계속 떴음. 금액 칸이 바뀌면(직접 편집, 재처리 결과 채택, 자동 검수) 현재 값으로 다시 판정.",
      ja: "金額警告（差引整合性・最終支払検証・金額整合性）は抽出時に一度だけ作られ、金額を正しく直しても残り、納品前品質チェックの金額警告も表示され続けていた。金額欄が変わると（直接編集・再処理結果の採用・自動検収）、現在の値で再判定。",
    },
  },
  {
    id: "2026-1015-agent-master-0828",
    date: "2026-10-15",
    category: "requirement",
    status: "in_progress",
    title: {
      ko: "최신 현지 대리인 마스터(08-28) 대조 — 1089 추가, 71xxx 23곳 임시 등록 (발주처 확인 중)",
      ja: "最新の現地代理人マスタ（08-28）との照合 — 1089を追加、71xxxの23社を仮登録（発注元に確認中）",
    },
    detail: {
      ko: "08-28 마스터에만 있던 71089 Dr. Schon, Neymeyr & Partner(EUR)를 추가하고, 영문 대리인명이 있는 71xxx 23곳을 임시로 등록(파일명 코드로만 판별). 71018 PT Tilleke & Gibbins Indonesia는 지불처명이 비어 있어, 이름이 전혀 없는 64곳과 함께 발주처 확인 후 추가. 08-28 마스터의 지불처명은 송금용 표기('&'→'AND', 계좌명)라 대리인명 기준으로 쓰지 않음.",
      ja: "08-28マスタにのみあった71089 Dr. Schon, Neymeyr & Partner（EUR）を追加し、英語の代理人名がある71xxxの23社を仮登録（ファイル名のコードでのみ判別）。71018 PT Tilleke & Gibbins Indonesiaは支払先名が空のため、名称がまったくない64社とあわせて発注元に確認後に追加。08-28マスタの支払先名は送金用の表記（「&」→「AND」、口座名）のため、代理人名の基準には使わない。",
    },
  },
  {
    id: "2026-1014-auto-review",
    date: "2026-10-14",
    category: "requirement",
    status: "done",
    title: {
      ko: "자동 검수 — 업로드 때 켜면 AI가 1차 검수부터 2차 완료까지 처리 + 완료 보고 메일",
      ja: "自動検収 — アップロード時にオンにすると、AIが一次検査から二次完了まで処理 + 完了報告メール",
    },
    detail: {
      ko: "업로드 화면에서 '자동 검수'를 켜면 추출이 끝난 뒤 AI가 1차 검수를 하고, 지적이 있으면 그 칸을 다시 읽어 고친 뒤 1차 검수를 한 번 더 함. 문제가 없으면 2차 검수 완료로 처리하고, 해결하지 못한 건은 이유와 함께 '추가검토 필요'로 남김. 이미 2차 완료·반려·사람이 손댄 건은 건드리지 않음. 작업 화면에 건별 자동 검수 상태가 보이고, 의뢰 전체가 끝나면 업로드한 사람의 로그인 이메일로 결과 요약(건수, 사람 확인 목록, 자동 수정 목록)을 보냄.",
      ja: "アップロード画面で「自動検収」をオンにすると、抽出後にAIが一次検査を行い、指摘があればその項目を読み直して修正したうえで、もう一度一次検査を行う。問題がなければ二次検査完了として処理し、解決できなかった案件は理由とともに「要追加確認」として残す。二次完了済み・却下・人が操作した案件には触れない。作業画面に案件ごとの自動検収状態が表示され、依頼全体が終わるとアップロードした人のログインメールに結果の要約（件数、要確認リスト、自動修正リスト）を送る。",
    },
  },
  {
    id: "2026-1014-swift-format-check",
    date: "2026-10-14",
    category: "issue",
    status: "done",
    title: {
      ko: "SWIFT 코드 형식(8·11자리 코드 하나)을 1차 검수가 지적",
      ja: "SWIFTコードの形式（8桁・11桁のコード1つ）を一次検査が指摘",
    },
    detail: {
      ko: "코드 두 개를 붙인 값(예: 'DRESDEFF700 / HYVEDEMMXXX')을 AI 검토가 통과시킨 사례가 있어, 8자리 또는 11자리 코드 하나가 아니면 코드 판정으로 지적. 자동 검수는 이 지적이 있으면 SWIFT만 다시 읽고, 값을 비우는 결과는 채택하지 않음. 자리 수가 아니라 형식만 봄 — 8자리·11자리 모두 정상.",
      ja: "コードを2つつなげた値（例: 「DRESDEFF700 / HYVEDEMMXXX」）をAIレビューが合格させた事例があり、8桁または11桁のコード1つでなければコード判定で指摘。自動検収はこの指摘があるとSWIFTのみ読み直し、値を空にする結果は採用しない。桁数ではなく形式のみ確認 — 8桁・11桁どちらも正常。",
    },
  },
  {
    id: "2026-1014-agent-name-currency-marker",
    date: "2026-10-14",
    category: "requirement",
    status: "done",
    title: {
      ko: "대리인명 끝의 통화 표시는 납품 CSV에서 자동으로 뺌 + 지불처 마스터와 대리인 마스터 동기화",
      ja: "代理人名末尾の通貨表示は納品CSVで自動的に除去 + 支払先マスタと代理人マスタを同期",
    },
    detail: {
      ko: "지불처 마스터(支払先マスタ_20260803更新)가 같은 회사의 통화별 계좌를 구분하려고 정식명 끝에 붙인 (USD)·(YEN)·(￡) 같은 표시를 CSV로 나갈 때 뺌(DB·편집 화면 값은 그대로). 검수 담당자가 직접 지우던 작업을 없앰. 함께: 대리인 마스터를 지불처 마스터와 대조해 이름 3건(425 Watson & Band …Ltd.(USD), 443 SHANGHAI PATENT & TRADEMARK LAW OFFICE LLC, 145 GABRIEL PATENT&TRADEMARK OFFICE)을 맞추고, 빠져 있던 164 CPA Global Limited(GBP)를 추가.",
      ja: "支払先マスタ（支払先マスタ_20260803更新）が同じ会社の通貨別口座を区別するために正式名称の末尾に付けた(USD)・(YEN)・(￡)などの表示を、CSV出力時に除去（DB・編集画面の値はそのまま）。担当者が手作業で消していた作業をなくす。あわせて: 代理人マスタを支払先マスタと照合し、名称3件（425 Watson & Band …Ltd.(USD)、443 SHANGHAI PATENT & TRADEMARK LAW OFFICE LLC、145 GABRIEL PATENT&TRADEMARK OFFICE）を合わせ、抜けていた164 CPA Global Limited（GBP）を追加。",
    },
  },
  {
    id: "2026-1013-billing-empty-not-rejected",
    date: "2026-10-13",
    category: "issue",
    status: "done",
    title: {
      ko: "청구처를 읽지 못한 건은 자동 반려하지 않음 — '청구처 비어 있음'으로 확인",
      ja: "請求先を読み取れなかった案件は自動で返却しない —「請求先が空」として確認",
    },
    detail: {
      ko: "운영 기록(2026-09-03 이후 2,049건)에서 사람이 반려를 해제한 51건 중 39건이 '請求先の不一致'였고, 그중 25건은 처음에 청구처가 비어 있었음(읽지 못한 것을 TTDC 아님으로 판정). 추출 때의 반려 판정을 납품 전 품질 검사와 같은 기준으로 맞춤: 청구처가 비어 있으면 반려하지 않고 화면의 '청구처 비어 있음'으로 확인. 청구처 글자가 TTDC 계열 표기면 LLM 판정과 관계없이 TTDC로 봄. 청구처 글자와 LLM 판정이 모두 TTDC가 아닐 때만 '請求先の不一致'로 반려.",
      ja: "運用記録（2026-09-03以降の2,049件）で、人が返却を解除した51件のうち39件が「請求先の不一致」で、そのうち25件は最初に請求先が空だった（読み取れなかったものをTTDC以外と判定）。抽出時の返却判定を納品前品質チェックと同じ基準に統一: 請求先が空なら返却せず、画面の「請求先が空」で確認。請求先の文字がTTDC系の表記なら、LLMの判定に関係なくTTDCとみなす。請求先の文字とLLMの判定がどちらもTTDC以外のときのみ「請求先の不一致」で返却。",
    },
  },
  {
    id: "2026-1012-agent-rules-transfer",
    date: "2026-10-12",
    category: "requirement",
    status: "done",
    title: {
      ko: "회사별 규칙 내보내기/가져오기 — 로컬에서 테스트 후 운영에 동기화",
      ja: "会社別ルールのエクスポート/インポート — ローカルで検証後に本番へ同期",
    },
    detail: {
      ko: "관리자 > 대리인 화면에 [규칙 내보내기]/[규칙 가져오기] 추가. 회사별 추출 규칙·검토 규칙 전체를 JSON 파일로 받고, 다른 서버에 올리면 바뀌는 회사·규칙을 전/후로 미리 보여 준 뒤 고른 회사만 반영(일반 대리인 수정과 같은 경로라 변경 이력이 남고, 처리 서버에는 1분 안에 적용). 파일에 없는 회사는 건드리지 않음. 함께 수정: 인보이스 번호 규칙 키를 관리자 편집기와 같은 agent_invoice_number 로 통일하고 옛 키(invoice_number)는 같은 필드로 처리 — 규칙 제안을 승인할 때 한 필드에 규칙이 두 개 생기던 문제 방지.",
      ja: "管理画面 > 代理人に［ルールをエクスポート］/［ルールをインポート］を追加。会社別の抽出ルール・レビュールールをすべてJSONファイルで取得し、別のサーバーにアップロードすると変更される会社・ルールを変更前/後でプレビューし、選択した会社のみ反映（通常の代理人編集と同じ経路のため変更履歴が残り、処理サーバーには1分以内に適用）。ファイルにない会社は変更しない。あわせて修正: インボイス番号ルールのキーを管理画面の編集器と同じ agent_invoice_number に統一し、旧キー（invoice_number）は同じフィールドとして扱う — ルール提案の承認時に1フィールドにルールが2つできる問題を防止。",
    },
  },
  {
    id: "2026-1008-invoice-underscore-save",
    date: "2026-10-08",
    category: "issue",
    status: "done",
    title: {
      ko: "代理人インボイスNo: 저장 시에도 '_'→'-' 변환 + 남아 있으면 AI 최종검토가 지적",
      ja: "代理人インボイスNo: 保存時にも「_」→「-」に変換 + 残っていればAI最終レビューが指摘",
    },
    detail: {
      ko: "AI 수정 요청이 원문대로 '23IM03545_22'를 넣었고, AI 최종검토는 'PDF와 일치'로 통과시킴(납품 CSV는 출력 필터로 '-'였음). 규칙 정의를 한 곳으로 모으고 자동 추출·AI 수정 반영·직접 편집 저장 모두 '_'를 '-'로 바꿔 저장. 값에 '_'가 남아 있으면 AI 최종검토가 LLM 판단이 아닌 코드 판정으로 지적.",
      ja: "AI修正依頼が原文どおり「23IM03545_22」を入れ、AI最終レビューは「PDFと一致」として合格させた（納品CSVは出力フィルターで「-」だった）。ルール定義を1か所にまとめ、自動抽出・AI修正の反映・直接編集の保存のすべてで「_」を「-」に変換して保存。値に「_」が残っていれば、AI最終レビューがLLMの判断ではなくコード判定で指摘。",
    },
  },
  {
    id: "2026-1008-credit-note-review",
    date: "2026-10-08",
    category: "issue",
    status: "done",
    title: {
      ko: "credit note 음수 금액을 검토 단계가 '부호 오류'로 지적하던 문제",
      ja: "credit noteのマイナス金額をレビュー段階が「符号誤り」と指摘していた問題",
    },
    detail: {
      ko: "credit note(환불 문서)는 규칙상 금액을 음수로 출력하는데(9/4 피드백), 2차 AI 검토와 금액 분석이 이 규칙을 몰라 'PDF는 1,685.00인데 -1685.00'이라고 지적함. 두 단계의 공통 지침에 'credit note는 음수 출력 — 부호는 지적하지 말고 금액 크기만 비교, 일반 청구서인데 음수일 때만 지적'을 추가.",
      ja: "credit note（返金文書）はルール上金額をマイナスで出力するが（9/4フィードバック）、2次AIレビューと金額分析がこのルールを知らず「PDFは1,685.00なのに-1685.00」と指摘していた。両段階の共通指針に「credit noteはマイナス出力 — 符号は指摘せず金額の大きさのみ比較、通常の請求書でマイナスの場合のみ指摘」を追加。",
    },
  },
  {
    id: "2026-1008-swift-reread",
    date: "2026-10-08",
    category: "issue",
    status: "done",
    title: {
      ko: "SWIFT 누락: OCR 한 글자 오인식 시 PDF 이미지로 SWIFT 재판독",
      ja: "SWIFT欠落: OCRの1文字誤認識時にPDF画像でSWIFTを再読み取り",
    },
    detail: {
      ko: "필드 추출은 OCR 텍스트만 보기 때문에, OCR이 SWIFT를 한 글자 틀리게 읽으면(BNPFRPPXXX 10자리, CITIUS3XXX, HSBCCHKHHHKH 12자리) 8·11자리 형식 검사에서 메모 없이 버려져 빈 값이 됨. SWIFT가 비었는데 원문에 SWIFT/BIC 표기가 있으면 PDF 이미지에서 SWIFT만 다시 읽고, 결과를 '확인 필요'에 남김(실패 시 'SWIFT 형식 오류' 메모). 로컬 검증 3건 모두 올바른 SWIFT로 복구.",
      ja: "フィールド抽出はOCRテキストのみを見るため、OCRがSWIFTを1文字誤読すると（BNPFRPPXXX 10桁、CITIUS3XXX、HSBCCHKHHHKH 12桁）8・11桁の形式チェックでメモなしに捨てられ空欄になっていた。SWIFTが空で原文にSWIFT/BIC表記があれば、PDF画像からSWIFTのみ再読み取りし、結果を「要確認」に残す（失敗時は「SWIFT形式エラー」メモ）。ローカル検証3件すべて正しいSWIFTに復旧。",
    },
  },
  {
    id: "2026-1008-agent-rules-review",
    date: "2026-10-08",
    category: "issue",
    status: "done",
    title: {
      ko: "회사별 추출 규칙을 AI 최종검토 기준으로도 사용 (ESTUDIO MUÑIZ 인보이스 번호 끝 '/')",
      ja: "会社別の抽出ルールをAI最終レビューの基準にも使用（ESTUDIO MUÑIZ インボイス番号末尾の「/」）",
    },
    detail: {
      ko: "ESTUDIO MUÑIZ는 인보이스 번호를 'REF. INVOICE: 189822E-233/'처럼 끝에 '/'를 붙여 표기하는데, LLM이 이를 간헐적으로 빠뜨림(3건 중 1건). 편집 화면 원문 대조는 OCR 기호 오류를 견디도록 기호를 무시하고, AI 최종검토는 '글자·숫자 오타만 지적' 지침이라 누락이 '일치/통과'로 판정됨. 회사별 예외는 관리자 화면 > 대리인 > 추출 규칙 한 곳에만 넣고, AI 최종검토도 같은 규칙을 '일반 지침보다 우선하는 회사 출력 규칙'으로 읽어 위반 시 지적하도록 변경. 로컬 검증: 규칙 적용 시 추출 3/3 정상('/' 포함), '/'를 지운 값은 AI 최종검토가 지적. 기존 검토 규칙(상습 오류 필드 메모)은 그대로 유지.",
      ja: "ESTUDIO MUÑIZ はインボイス番号を「REF. INVOICE: 189822E-233/」のように末尾に「/」を付けて表記するが、LLMが断続的に欠落（3件中1件）。編集画面の原文照合はOCRの記号誤りに耐えるよう記号を無視し、AI最終レビューは「文字・数字の誤記のみ指摘」の指針のため、欠落が「一致/合格」と判定された。会社別の例外は管理画面 > 代理人 > 抽出ルールの1か所にだけ登録し、AI最終レビューも同じルールを「一般指針より優先する会社の出力ルール」として読み、違反時に指摘するよう変更。ローカル検証: ルール適用時は抽出3/3正常（「/」含む）、「/」を消した値はAI最終レビューが指摘。既存のレビュールール（頻出誤りフィールドのメモ）はそのまま維持。",
    },
  },
  {
    id: "2026-1008-ref-number-rules",
    date: "2026-10-08",
    category: "issue",
    status: "done",
    title: {
      ko: "TTDC 案件番号 출력·검수 기준 적용 — 형식 확대·자동 보정·검수 경고",
      ja: "TTDC案件番号の出力・検収基準を適用 — 形式拡大・自動補正・検収警告",
    },
    detail: {
      ko: "정식 형식을 TFN######-XX/-XXX##/-XX##/-XXX, M?####-####-XX(-##, -##-##), D?####-####(-XX)로 확정하고 추출·출력·검수가 같은 정의를 사용. 접미가 잘리던 MS2025-0029-PH-01-12 등을 그대로 인정. 자동 보정(원문 근거만): 번호 하나만 남김(뒤에 붙은 참조번호 제거), 복수 표기는 첫 번호+공통 접미(DA2019-0205,0206-CN → DA2019-0205-CN, 번호만 추출돼도 원문에서 보완), TTFN·TFM·TPN→TFN, 특허 접미 숫자 자리의 O→0. 같은 규칙을 CSV 출력 시에도 적용해 수동 편집값도 정리(형식 토큰이 없으면 그대로 두고 경고). 납품 전 품질 검사 경고 4종 추가: 형식 불일치, 출력 시 자동 보정(이전→이후 표시), 의장 번호 국가코드 없음, 번호 자체가 파일명과 다름(파일명은 힌트로만 사용). 기준: docs/출력_검수_기준.md",
      ja: "正式形式を TFN######-XX/-XXX##/-XX##/-XXX、M?####-####-XX(-##, -##-##)、D?####-####(-XX) に確定し、抽出・出力・検収が同じ定義を使用。接尾が切れていた MS2025-0029-PH-01-12 などをそのまま認める。自動補正（原文根拠のみ）: 番号1つだけを残す（後ろに付いた参照番号を除去）、複数表記は先頭番号＋共通接尾（DA2019-0205,0206-CN → DA2019-0205-CN、番号のみ抽出されても原文から補完）、TTFN・TFM・TPN→TFN、特許接尾の数字位置の O→0。同じルールをCSV出力時にも適用し手動編集値も整理（形式トークンがなければそのまま残して警告）。納品前品質チェックに警告4種を追加: 形式不一致、出力時の自動補正（変更前→変更後を表示）、意匠番号の国コードなし、番号自体がファイル名と異なる（ファイル名はヒントとしてのみ使用）。基準: docs/출력_검수_기준.md",
    },
  },
  {
    id: "2026-1008-invoice-no-output",
    date: "2026-10-08",
    category: "issue",
    status: "done",
    title: {
      ko: "代理人インボイスNo: CSV 출력 시 '_'→'-' 재적용",
      ja: "代理人インボイスNo: CSV出力時に「_」→「-」を再適用",
    },
    detail: {
      ko: "자동 추출은 '_'를 '-'로 바꾸지만, 1차 검수 수동 편집에서 원문대로 '_'를 다시 넣은 사례가 확인됨(인보이스 번호 수동 편집 42건 중 16건). 저장 경로와 관계없이 CSV(납품/확인용, 미리보기, 의뢰 단위) 출력 직전에 '_'→'-'를 한 번 더 적용. 편집 화면·DB 값은 바꾸지 않으며, '_' 외 기호(예: PTDN1468/2026의 '/')는 원문대로 유지.",
      ja: "自動抽出では「_」を「-」に変換するが、1次検収の手動編集で原文どおり「_」に戻す事例を確認（インボイス番号の手動編集42件中16件）。保存経路に関係なく、CSV（納品/確認用、プレビュー、依頼単位）出力直前に「_」→「-」をもう一度適用。編集画面・DBの値は変更せず、「_」以外の記号（例: PTDN1468/2026 の「/」）は原文どおり維持。",
    },
  },
  {
    id: "2026-1008-design-country",
    date: "2026-10-08",
    category: "inquiry",
    status: "pending",
    title: {
      ko: "의장(D 계열) 案件番号 국가코드 필수 여부 (발주처 확인 대기)",
      ja: "意匠（D系）案件番号の国コード必須可否（発注元へ確認中）",
    },
    detail: {
      ko: "국가코드 없는 단일 의장 번호 28건 중 26건은 인보이스 원문 어디에도 국가코드가 없음(파일명에만 기재). 원문 근거 원칙에 따라 현재는 그대로 출력하고 '의장 번호 국가코드 없음' 경고만 표시. 국가코드가 필수라면 대리인 마스터에 국가 정보를 추가해 자동 보완하는 방식을 검토.",
      ja: "国コードのない単独意匠番号28件中26件は、インボイス原文のどこにも国コードがない（ファイル名にのみ記載）。原文根拠の原則により現在はそのまま出力し「意匠番号の国コードなし」警告のみ表示。国コードが必須であれば、代理人マスターに国情報を追加して自動補完する方式を検討。",
    },
  },
  {
    id: "2026-0814-request-layer",
    date: "2026-08-14",
    category: "requirement",
    status: "done",
    title: {
      ko: "최상위 관리 단위 '의뢰(Request)' 도입 — ZIP 업로드 폴더별 작업 분할",
      ja: "最上位管理単位「依頼(Request)」の導入 — ZIPアップロードでフォルダ別に作業分割",
    },
    detail: {
      ko: "작업(Job) 위에 '의뢰(Request)' 단위를 신설. ZIP을 업로드하면 내부를 탐색해 PDF가 든 폴더마다 작업을 자동 생성하고, 의뢰 단위로 파일 수·진행률·검수완료 수를 집계. PDF 직접 업로드는 단일 작업 의뢰로 처리(하위호환). 기존 작업 42건은 1:1 의뢰로 백필. 의뢰 단위 통합 CSV(납품/확인×정상/반려) 다운로드·미리보기 제공. ZIP 처리 시 zip-slip 방지, __MACOSX/.DS_Store 등 잡음 제외, 파일/폴더명 NFC 정규화 적용.",
      ja: "作業(Job)の上に「依頼(Request)」単位を新設。ZIPをアップロードすると内部を探索し、PDFが入ったフォルダごとに作業を自動生成、依頼単位でファイル数・進捗・検収完了数を集計。PDF直接アップロードは単一作業の依頼として処理(後方互換)。既存作業42件は1:1依頼へバックフィル。依頼単位のまとめCSV(納品/確認×正常/却下)ダウンロード・プレビューを提供。ZIP処理時はzip-slip防止、__MACOSX/.DS_Storeなどのノイズ除外、ファイル/フォルダ名のNFC正規化を適用。",
    },
  },
  {
    id: "2026-0811-swift-beneficiary",
    date: "2026-08-11",
    category: "issue",
    status: "done",
    title: {
      ko: "SWIFT: 수취은행 대신 중계/코레스은행 SWIFT 차용 검출",
      ja: "SWIFT: 受取銀行ではなく中継/コルレス銀行SWIFTの流用を検出",
    },
    detail: {
      ko: "송금 지시에 수취은행(受取銀行/Beneficiary's Bank) 외에 중계은행(中継銀行/Correspondent/Intermediary Bank)의 SWIFT가 함께 표기된 경우, 마스터 통화 계좌의 '수취은행' SWIFT가 아니라 중계은행 SWIFT를 잘못 차용하는 케이스가 있음. AI 최종검토(chat-vl-pro) 프롬프트에 '수취은행 SWIFT여야 하며 중계/코레스은행 SWIFT면 지적' 규칙을 추가. (전사 오타/유효성 판단이 아닌 '올바른 은행 선택' 검수) 추출 단계 자동 선택 로직 보강은 추후 과제.",
      ja: "送金指示に受取銀行（Beneficiary's Bank）以外に中継銀行（Correspondent/Intermediary Bank）のSWIFTも併記される場合、マスター通貨口座の「受取銀行」SWIFTではなく中継銀行SWIFTを誤って流用するケースがある。AI最終レビュー（chat-vl-pro）プロンプトに「受取銀行SWIFTであるべきで、中継/コルレス銀行SWIFTなら指摘」ルールを追加。（転記誤字/有効性判断ではなく「正しい銀行選択」の検収）抽出段階の自動選択ロジック強化は今後の課題。",
    },
  },
  {
    id: "2026-0811-ai-review-rules",
    date: "2026-08-11",
    category: "requirement",
    status: "done",
    title: {
      ko: "1차 검수 후 AI 최종검토를 '프로젝트 출력규칙' 기준으로 재정의",
      ja: "1次検収後のAI最終レビューを「プロジェクト出力ルール」基準へ再定義",
    },
    detail: {
      ko: "AI 검토가 일반 은행/회계 감사가 아니라 '출력 규칙 순응 + 문서 전사 충실도' 확인이 되도록 하이브리드로 개편. 12개 상시점검 중 #1 파일명형식·#2 현지코드형식·#5 마스터 대리인명/코드 정합·#9 합계검산·#10 통화단위는 코드가 마스터(AGENT_MAP)/정규식으로 확정 판정하고, 전사오타·누락 vs 미식별·실제 최종지불금액·날짜순서 정합은 LLM(chat-vl-pro)이 마스터 근거를 참조해 판정. CNY↔USD 환산·SWIFT 유효성·원천징수 임의가정·정식명 형식 트집 등 오탐은 DO-NOT로 금지. 결과를 issues(문제)/checks(정상확인)로 분리해 issues가 있을 때만 추가검토 필요.",
      ja: "AIレビューを一般的な銀行/会計監査ではなく「出力ルール順守＋文書転記の忠実性」確認となるようハイブリッドで再構成。12の常時チェックのうち #1ファイル名形式・#2現地コード形式・#5マスター代理人名/コード整合・#9合計検算・#10通貨単位はコードがマスター(AGENT_MAP)/正規表現で確定判定し、転記誤字・欠落 vs 未識別・実際の最終支払金額・日付順序整合はLLM(chat-vl-pro)がマスター根拠を参照して判定。CNY↔USD換算・SWIFT有効性・源泉徴収の任意仮定・正式名の形式指摘などの誤検知はDO-NOTで禁止。結果をissues(問題)/checks(正常確認)に分離し、issuesがある時のみ要追加レビュー。",
    },
  },
  {
    id: "2026-0811-agent-country",
    date: "2026-08-11",
    category: "issue",
    status: "pending",
    title: {
      ko: "date_formats.json의 agent_country 매핑 보강 (보류)",
      ja: "date_formats.jsonのagent_countryマッピング補強（保留）",
    },
    detail: {
      ko: "날짜순서(MDY/DMY/YMD) 결정 시 agent_country에 CN/JP/KR/TW 등 대리인 국가가 거의 비어 있어 default(DMY)로 폴백됨(예: CCPIT(026, 중국)이 DMY로 조회). 로직 문제는 아니며 파이프라인 실제 정규화도 동일 규칙 사용. 마스터 기준으로 국가를 채우면 #11 날짜 정확도 향상. 우선순위 보류, 추후 진행.",
      ja: "日付順序(MDY/DMY/YMD)決定時、agent_countryにCN/JP/KR/TW等の代理人国が殆ど未登録でdefault(DMY)にフォールバック（例: CCPIT(026,中国)がDMYで参照）。ロジックの問題ではなくパイプライン実処理も同一規則を使用。マスター基準で国を埋めれば#11日付精度が向上。優先度は保留、後日対応。",
    },
  },
  {
    id: "2026-0807-dualcode",
    date: "2026-08-07",
    category: "inquiry",
    status: "pending",
    title: {
      ko: "이중코드 파일명의 現地代理人コード 처리 (발주처 확인 대기)",
      ja: "二重コードファイル名の現地代理人コード処理（発注元へ確認中）",
    },
    detail: {
      ko: "20260722済_53件 中 파일명에 대리인 코드가 2개인 4건(예: 70026-01_70180-01_Tilleke…). 현재 첫 번째 코드(70026-01=CCPIT) 기준으로 납품됨. 파일명·인보이스 실제 발행처는 두 번째 코드(Tilleke/KINGSOUND). 어느 코드 기준이 맞는지 발주처 확인 후 확정. 代理人名은 코드 기준으로 자동 연동됨.",
      ja: "20260722済_53件のうち、ファイル名に代理人コードが2つある4件（例: 70026-01_70180-01_Tilleke…）。現在は1つ目のコード（70026-01=CCPIT）で納品。ファイル名・インボイスの実発行元は2つ目のコード（Tilleke/KINGSOUND）。どちらが正しいか発注元へ確認後に確定。代理人名はコード基準で自動連動。",
    },
  },
  {
    id: "2026-0807-underscore",
    date: "2026-08-07",
    category: "issue",
    status: "done",
    title: {
      ko: "파일명 언더스코어 누락 tolerance",
      ja: "ファイル名アンダースコア欠落の許容",
    },
    detail: {
      ko: "날짜와 코드 사이 '_' 누락 시(26072170260-01_…) 첫 세그먼트에서 코드(70260-01) 복구. csv_writer/agent_detection 양쪽 반영.",
      ja: "日付とコードの間の「_」欠落時（26072170260-01_…）、先頭セグメントからコード（70260-01）を復元。csv_writer/agent_detection 両方に反映。",
    },
  },
  {
    id: "2026-0807-ttfn",
    date: "2026-08-07",
    category: "issue",
    status: "done",
    title: {
      ko: "ref_number OCR 오타 보정 (TTFN→TFN)",
      ja: "ref_number OCR 誤字補正（TTFN→TFN）",
    },
    detail: {
      ko: "TTDC 案件番号의 흔한 OCR 오타(TTFN→TFN) 자동 교정 및 공백 제거. 검증 직전 적용.",
      ja: "TTDC 案件番号のよくあるOCR誤字（TTFN→TFN）を自動補正し空白を除去。検証直前に適用。",
    },
  },
  {
    id: "2026-0803-swift-single",
    date: "2026-08-03",
    category: "requirement",
    status: "done",
    title: {
      ko: "복수 SWIFT → 마스터 통화 기준 1개 선택",
      ja: "複数SWIFT → マスター通貨基準で1つ選択",
    },
    detail: {
      ko: "지급처 마스터 통화와 일치하는 SWIFT 1개만 출력. 콤마 병기 금지. 대문자·공백제거·8/11자리 검증.",
      ja: "支払先マスター通貨と一致するSWIFTを1つのみ出力。カンマ併記禁止。大文字・空白除去・8/11桁検証。",
    },
  },
  {
    id: "2026-0803-converted-total",
    date: "2026-08-03",
    category: "requirement",
    status: "done",
    title: {
      ko: "대상통화 환산액 우선 (請求金額合計)",
      ja: "対象通貨換算額を優先（請求金額合計）",
    },
    detail: {
      ko: "마스터 통화(예: USD)와 청구 통화가 다르면, 문서에 명시된 환산 합계(USD)를 請求金額（合計）로 사용. CCPIT/Tilleke/集佳 등.",
      ja: "マスター通貨（例: USD）と請求通貨が異なる場合、文書に明記された換算合計（USD）を請求金額（合計）として使用。CCPIT/Tilleke/集佳 等。",
    },
  },
  {
    id: "2026-0220-sort",
    date: "2026-02-20",
    category: "requirement",
    status: "done",
    title: {
      ko: "산출물 정렬: 파일명(PDF名) 오름차순",
      ja: "成果物ソート: ファイル名（PDF名）昇順",
    },
    detail: {
      ko: "CSV 및 UI 모두 PDF名 오름차순으로 정렬 후 NO. 부여. (구 '앞 6자리' 기준 폐기)",
      ja: "CSV・UIともにPDF名昇順で並べ替えた上でNO.を付与。（旧「先頭6桁」基準は廃止）",
    },
  },
  {
    id: "2026-0220-nfc",
    date: "2026-02-20",
    category: "requirement",
    status: "done",
    title: {
      ko: "PDF名: 원본 유지 + CSV만 NFC 정규화",
      ja: "PDF名: 原本維持 + CSVのみNFC正規化",
    },
    detail: {
      ko: "CSV 출력 시 결합문자(NFD)를 NFC로 정규화해 표기 통일. 디스크 파일명 rename은 금지.",
      ja: "CSV出力時に結合文字（NFD）をNFCへ正規化し表記を統一。ディスク上のファイル名リネームは禁止。",
    },
  },
  {
    id: "2026-0220-reject",
    date: "2026-02-20",
    category: "requirement",
    status: "done",
    title: {
      ko: "청구처 TTDC 불일치 → 반려 CSV 분리",
      ja: "請求先がTTDC不一致 → 返却CSVへ分離",
    },
    detail: {
      ko: "is_ttdc=false면 자동 반려(返却_インボイスOCR結果.csv, 사유 '청구처 불일치'). 振込先なし 자동감지는 별도 과제.",
      ja: "is_ttdc=false の場合は自動返却（返却_インボイスOCR結果.csv、理由「請求先不一致」）。振込先なしの自動検知は別課題。",
    },
  },
  {
    id: "2026-0803-furikomi",
    date: "2026-08-03",
    category: "issue",
    status: "pending",
    title: {
      ko: "振込先なし(송금처 없음) 자동 반려",
      ja: "振込先なしの自動返却",
    },
    detail: {
      ko: "송금처(은행/SWIFT) 정보가 없는 건의 자동 반려는 미구현. 현재는 수기 처리. 향후 자동화 예정.",
      ja: "振込先（銀行/SWIFT）情報が無い件の自動返却は未実装。現状は手動対応。今後自動化予定。",
    },
  },
];
